// CustomerTransactionReport.jsx — "Show Customer Transaction" drill-down from Total Customers

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentsIcon from '@mui/icons-material/Payments';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { format } from 'date-fns';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../constants/constants';
// [CASH-MODE] UPI / Cash filter + indication
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeLabel, paymentModeParam, totalsByModeFromRows } from '../../utils/paymentMode';
import PaymentModeFilter from '../../components/PaymentModeFilter';
import PaymentModeChip from '../../components/PaymentModeChip';
import CustomerReportShell, {
  DateRangeFilter,
  ReportGrid,
  SectionCard,
  StatusChip,
  displayVpa,
  formatINR,
  formatISTDateTime,
  parseApiDate,
  useCanViewCustomerSensitive,
  validateDateRange,
} from '../../components/CustomerReportShell';

// Collection per mode = SUCCESS payments (debits) only. Uses the API `amount` key when present.
const collectionTotals = (apiTotals, rows) => {
  if (apiTotals && apiTotals.UPI && typeof apiTotals.UPI.amount === 'number' && apiTotals.CASH && typeof apiTotals.CASH.amount === 'number') {
    return apiTotals;
  }
  const paid = rows.filter((r) => r.type === 'DEBIT' && String(r.status).toUpperCase() === 'SUCCESS');
  return totalsByModeFromRows(paid, (r) => r.amount);
};

const CustomerTransactionReport = () => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE] API totalsByMode when provided
  const canViewSensitive = useCanViewCustomerSensitive();

  const { customerId } = useParams();

  useEffect(() => {
    fetchTransactionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, startDate, endDate, paymentMode]); // [CASH-MODE] refetch on mode change

  const fetchTransactionData = async () => {
    const rangeError = validateDateRange(startDate, endDate);
    if (rangeError) {
      setDialogMessage(rangeError);
      setOpenDialog(true);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/customerReport/customerTransaction`,
        {
          customerId,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          page: 1,
          limit: 1000,
          paymentMode: paymentModeParam(paymentMode), // [CASH-MODE] undefined for ALL
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      const data = response.data || {};
      setCustomerName(data.customerName || '');
      setTotalsByMode(data.totalsByMode || (data.summary && data.summary.totalsByMode) || null); // [CASH-MODE]
      setTransactions((data.transactions || []).map((item, index) => ({
        id: item.id || index + 1,
        serialNumber: index + 1,
        customerName: data.customerName || '',
        type: item.type || 'DEBIT',
        category: item.category || '',
        title: item.payeeName || item.shopName || item.title || '',
        description: item.description || '',
        amount: parseFloat(item.amount || 0),
        rawDate: item.dateTime,
        dateTime: formatISTDateTime(item.dateTime),
        payerVPA: item.payerVPA || '',
        payerName: item.payerName || '',
        payeeVPA: item.payeeVPA || '',
        status: item.gatewayResponseStatus || '',
        transactionId: item.gatewayTransactionId || '',
        paymentMode: item.paymentMode, // [CASH-MODE]
      })));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError((err.response && err.response.data && err.response.data.message) || 'An error occurred while fetching data.');
      setTransactions([]);
      setTotalsByMode(null);
    }
    setLoading(false);
  };

  const payments = transactions.filter((t) => t.type === 'DEBIT');
  const cashbacks = transactions.filter((t) => t.type === 'CREDIT');
  const totalPaid = payments.reduce((s, t) => s + t.amount, 0);
  const totalCashback = cashbacks.reduce((s, t) => s + t.amount, 0);
  const modeTotals = collectionTotals(totalsByMode, transactions);
  const grandTotal = transactions.reduce((sum, tx) => sum + tx.amount, 0);

  const handlePrint = () => {
    if (transactions.length === 0) {
      setDialogMessage('No data to print.');
      setOpenDialog(true);
      return;
    }
    const esc = (v) => String(v === undefined || v === null ? '' : v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    printWindow.document.write(`
      <html>
        <head>
          <title>Customer Transaction Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; padding: 20px; }
            .header, .report-info { text-align: center; margin-bottom: 20px; font-weight: bold; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .total { text-align: right; font-weight: bold; margin-top: 8px; }
          </style>
        </head>
        <body>
          <div class="header"><h1>Customer Transaction Report</h1></div>
          <div class="report-info">Customer: ${esc(customerName)}</div>
          <div class="report-info">From ${format(startDate, 'dd-MM-yyyy')} to ${format(endDate, 'dd-MM-yyyy')}</div>
          <table>
            <thead>
              <tr>
                <th>S.No.</th><th>Type</th><th>Shop / Description</th><th>Amount</th><th>Date & Time</th><th>Payer VPA</th><th>Status</th><th>Transaction ID</th><th>Mode</th>
              </tr>
            </thead>
            <tbody>
              ${transactions.map((tx) => `
                <tr>
                  <td>${tx.serialNumber}</td><td>${tx.type === 'CREDIT' ? 'Cashback' : 'Payment'}</td><td>${esc(tx.title)}</td>
                  <td>₹${tx.amount.toFixed(2)}</td><td>${esc(tx.dateTime)}</td><td>${esc(displayVpa(tx.payerVPA, canViewSensitive))}</td>
                  <td>${esc(tx.status)}</td><td>${esc(tx.transactionId)}</td><td>${paymentModeLabel(tx)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="total">Total Paid: ₹${totalPaid.toFixed(2)} (UPI ₹${(Number(modeTotals.UPI.amount) || 0).toFixed(2)} · Cash ₹${(Number(modeTotals.CASH.amount) || 0).toFixed(2)})</div>
          <div class="total">Cashback: ₹${totalCashback.toFixed(2)}</div>
          <div class="total">Grand Total: ₹${grandTotal.toFixed(2)}</div>
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const columns = [
    { field: 'serialNumber', headerName: '#', width: 60, sortable: false },
    { field: 'dateTime', headerName: 'Date & Time (IST)', width: 190, type: 'dateTime',
      valueGetter: (value, row) => parseApiDate(row.rawDate), valueFormatter: (value) => formatISTDateTime(value) },
    {
      field: 'type', headerName: 'Type', width: 120,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value === 'CREDIT' ? 'Cashback' : 'Payment'}
          color={p.value === 'CREDIT' ? 'success' : 'default'}
          variant={p.value === 'CREDIT' ? 'filled' : 'outlined'}
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      field: 'title', headerName: 'Shop / Description', flex: 1, minWidth: 200,
      renderCell: (p) => (
        <div style={{ lineHeight: 1.3, overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{p.value || '-'}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">{p.row.description}</Typography>
        </div>
      ),
    },
    {
      field: 'amount', headerName: 'Amount', width: 130, type: 'number',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontWeight: 800, color: p.row.type === 'CREDIT' ? 'success.dark' : '#0f172a', width: '100%', textAlign: 'right' }}>
          {p.row.type === 'CREDIT' ? '+ ' : ''}{formatINR(p.value)}
        </Typography>
      ),
    },
    // [CASH-MODE] UPI / Cash badge
    { field: 'paymentMode', headerName: 'Mode', width: 100, renderCell: (params) => <PaymentModeChip row={params.row} /> },
    { field: 'status', headerName: 'Status', width: 120, renderCell: (p) => <StatusChip label={p.value} /> },
    { field: 'payerVPA', headerName: 'Payer VPA', width: 190, valueGetter: (value) => displayVpa(value, canViewSensitive) },
    {
      field: 'transactionId', headerName: 'Transaction ID', width: 220,
      renderCell: (p) => <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{p.value || '-'}</Typography>,
    },
  ];

  return (
    <CustomerReportShell
      title="Customer Transactions"
      subtitle={`Payments and cashback · ${format(startDate, 'dd MMM yyyy')} to ${format(endDate, 'dd MMM yyyy')}`}
      icon={<ReceiptLongIcon />}
      customerId={customerId}
      customerName={customerName}
      actions={(
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={transactions.length === 0} sx={{ fontWeight: 700 }}>
          Print
        </Button>
      )}
      filters={(
        <DateRangeFilter startDate={startDate} endDate={endDate} onStartChange={setStartDate} onEndChange={setEndDate}>
          {/* [CASH-MODE] */}
          <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
        </DateRangeFilter>
      )}
      error={error}
      onRetry={fetchTransactionData}
      statsLoading={loading}
      stats={[
        { label: 'Total Paid', value: formatINR(totalPaid), hint: `${payments.length} successful payment${payments.length === 1 ? '' : 's'}`, color: 'primary', icon: <PaymentsIcon /> },
        { label: 'Collection · UPI', value: formatINR(modeTotals.UPI.amount), hint: `${modeTotals.UPI.count || 0} transaction${modeTotals.UPI.count === 1 ? '' : 's'}`, color: 'secondary', icon: <QrCode2Icon /> },
        { label: 'Collection · Cash', value: formatINR(modeTotals.CASH.amount), hint: `${modeTotals.CASH.count || 0} transaction${modeTotals.CASH.count === 1 ? '' : 's'}`, color: 'warning', icon: <LocalAtmIcon /> },
        { label: 'Cashback Earned', value: formatINR(totalCashback), hint: `${cashbacks.length} credit${cashbacks.length === 1 ? '' : 's'}`, color: 'success', icon: <CardGiftcardIcon /> },
      ]}
    >
      <SectionCard title="Transaction History" subtitle={`${transactions.length} record${transactions.length === 1 ? '' : 's'}`}>
        <ReportGrid
          rows={transactions}
          columns={columns}
          loading={loading}
          rowHeight={60}
          emptyTitle="No transactions found"
          emptyMessage="No payments or cashback for this customer in the selected dates."
        />
      </SectionCard>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Alert</DialogTitle>
        <DialogContent><Typography>{dialogMessage}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </CustomerReportShell>
  );
};

export default CustomerTransactionReport;
